// WP5 6단계 — 담당자 화면(C-4 배송정보현황·간트 자재파트너·마커) 공통 로직. 순수 함수만(React 의존 없음).
// 이 파일이 6단계 문구·분류 규칙의 단일 위치다. 문구는 940·003 기획서 표현, 없는 것은 '문구 초안'(지시서 §14 D9).
// 서버 신호·필수 값이 없거나 약속된 값 밖이면 폴백하지 않고 ShipParseError 로 던진다.
// 시각 값은 오프셋 없는 LocalDateTime 문자열 — new Date()로 UTC 해석하지 않고 문자열을 잘라 쓴다.

// ── 쿼리 키 ─────────────────────────────────────────────────────────────────
/** 6단계 전용 쿼리 키 접두. useInvalidateSignals 가 이 접두로 무효화한다. */
export const SHIP_QUERY_PREFIX = ['ship'] as const;

// ── 타입 (지시서 §3.2·§3.3) ─────────────────────────────────────────────────
export type ShipPhase = 'ACTION_NEEDED' | 'IN_PROGRESS' | 'NOT_YET' | 'RECEIVED';
export type ShipActionCode = 'REQUEST_NOW' | 'REREQUEST' | 'ASSIGN_FOREMAN' | 'SEND_ARRIVAL_NOTICE' | 'CALL_DRIVER' | 'VIEW_PHOTOS' | 'CONFIRM_RECEIPT';
export type ShipReasonCode = 'RECEIPT_DELAY' | 'SHIP_INFO_MISSING' | 'NO_FOREMAN' | 'NO_CONTACT' | 'SEND_FAILED' | 'RECEIPT_ISSUE';
export type ShipArrivalState = 'SENT' | 'NO_FOREMAN' | 'NO_CONTACT' | 'FAILED' | 'PENDING' | 'NOT_NEEDED';
export type ShipOrderStatusCode =
  | 'DRAFT' | 'REPLY_WAIT' | 'REREPLY_WAIT' | 'RESEND_NEEDED' | 'CONFIRMED' | 'DECLINED'
  | 'SHIP_WAIT' | 'SHIP_REPLIED' | 'RECEIVED' | 'REPLACED';

export interface ShipDelivery {
  orderDeliveryId: number | null;
  sentDtm: string | null;
  replyDtm: string | null;
  replyRound: number | null;
  arrivalDate: string | null;
  /** "HH:mm" 또는 "HH:mm:ss" */
  arrivalTime: string | null;
  prevArrivalDate: string | null;
  prevArrivalTime: string | null;
  driverName: string | null;
  driverPhone: string | null;
  photoCnt: number | null;
  issueYn: 'Y' | 'N' | null;
  issueNote: string | null;
  replyActorId: number | null;
}

export interface ShipReceipt {
  orderReceiptId: number | null;
  receiptDtm: string | null;
  receiptDate: string | null;
  receiptMethodCode: 'FOREMAN_LINK' | 'MANAGER' | 'WORK_REPORT' | string | null;
  photoCnt: number | null;
  qtyCheckedCode: 'NORMAL' | 'ISSUE' | string | null;
  issueNote: string | null;
  receiptActorId: number | null;
}

export interface ShipRequestInfo {
  orderSendId: number | null;
  attemptNo: number | null;
  sendMethodCode: 'AUTO' | 'MANUAL' | 'OFFLINE' | string | null;
  sentDtm: string | null;
  sendStatus: string | null;
  failReasonCode: string | null;
  expiresDtm: string | null;
  viewedDtm: string | null;
  closedReasonCode: string | null;
}

export interface ShipArrivalNotice {
  state: ShipArrivalState;
  orderSendId: number | null;
  sendTypeCode: string | null;
  sentDtm: string | null;
  sendStatus: string | null;
  failReasonCode: string | null;
  partnerId: number | null;
}

export interface ShipItemLine {
  itemSeq: number | null;
  itemName: string | null;
  itemSpec: string | null;
  itemQty: number | null;
  itemUnit: string | null;
}

export interface ShipDeliveryItem {
  orderVendorId: number;
  taskId: number;
  siteId: number | null;
  vendorId: number | null;
  vendorName: string;
  orderStatusCode: ShipOrderStatusCode;
  /** YYYY-MM-DD */
  deliveryDate: string | null;
  phaseCode: ShipPhase;
  actionNeededReasons: ShipReasonCode[];
  availableActions: ShipActionCode[];
  delivery: ShipDelivery | null;
  receipt: ShipReceipt | null;
  shipRequest: ShipRequestInfo | null;
  arrivalNotice: ShipArrivalNotice;
  foremanPartnerId: number | null;
  /** 조회 실패 시 null */
  foremanName: string | null;
  items: ShipItemLine[];
  itemSummary: string | null;
}

export interface ShipDeliveryList {
  summary: { actionNeeded: number; inProgress: number; received: number; notYet: number };
  items: ShipDeliveryItem[];
}

/** A1 원장 행(6단계가 쓰는 필드만) */
export interface ShipLedgerRow {
  orderVendorId: number;
  taskId: number;
  vendorId: number | null;
  vendorName: string;
  orderStatusCode: ShipOrderStatusCode;
  orderDate: string | null;
  deliveryDate: string | null;
  receipt: { photoCnt: number | null; receiptDate: string | null; receiptMethodCode: string | null; qtyCheckedCode: string | null } | null;
}

export interface ShipPhoto {
  orderPhotoId: number;
  photoTypeCode: 'LOAD' | 'UNLOAD';
  seq: number;
  fileId: number | string | null;
  createdAt: string | null;
}

export type ShipArrivalNoticeResultCode = 'SENT' | 'NO_FOREMAN' | 'UNCHANGED';
export interface ShipArrivalNoticeResult { result: ShipArrivalNoticeResultCode }

export interface ShipRequestResult {
  orderSendId: number | null;
  attemptNo: number | null;
  sendTypeCode: string | null;
  sendMethodCode: string | null;
  orderStatusCode: string | null;
  sentDtm: string | null;
  expiresDtm: string | null;
}

export interface ShipReceiptBody { qtyCheckedCode: 'NORMAL' | 'ISSUE'; issueNote?: string; receiptDate?: string }
export interface ShipReceiptResult {
  orderReceiptId: number | null;
  orderStatusCode: string | null;
  receiptDtm: string | null;
  receiptDate: string | null;
  qtyCheckedCode: string | null;
}

/** C9 본문 — 없는 필드=변경 없음, null=비움 */
export interface ShipOrderScheduleBody { orderDate?: string | null; deliveryDate?: string | null }
export interface ShipOrderScheduleResult {
  taskId: number;
  orderDate: string | null;
  deliveryDate: string | null;
  affected: unknown;
  warnings: unknown[];
}

// ── 파서 (폴백 금지) ────────────────────────────────────────────────────────
export class ShipParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShipParseError';
  }
}

type Rec = Record<string, unknown>;

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function fail(what: string): never {
  throw new ShipParseError(`배송정보 응답 형태가 약속과 다름: ${what}`);
}
function reqRec(v: unknown, what: string): Rec {
  return isRec(v) ? v : fail(what);
}
function reqNum(o: Rec, k: string, what: string): number {
  const v = o[k];
  return typeof v === 'number' && Number.isFinite(v) ? v : fail(`${what}.${k}`);
}
function reqStr(o: Rec, k: string, what: string): string {
  const v = o[k];
  return typeof v === 'string' ? v : fail(`${what}.${k}`);
}
/** 서버 규격상 비어도 정상인 값: 없음·null → null. 있는데 타입이 다르면 던진다. */
function optStr(o: Rec, k: string, what: string): string | null {
  const v = o[k];
  if (v === undefined || v === null) return null;
  return typeof v === 'string' ? v : fail(`${what}.${k}`);
}
function optNum(o: Rec, k: string, what: string): number | null {
  const v = o[k];
  if (v === undefined || v === null) return null;
  return typeof v === 'number' && Number.isFinite(v) ? v : fail(`${what}.${k}`);
}
function optRecOrNull(o: Rec, k: string, what: string): Rec | null {
  const v = o[k];
  if (v === undefined || v === null) return null;
  return isRec(v) ? v : fail(`${what}.${k}`);
}

const PHASES: readonly ShipPhase[] = ['ACTION_NEEDED', 'IN_PROGRESS', 'NOT_YET', 'RECEIVED'];
const ACTIONS: readonly ShipActionCode[] = ['REQUEST_NOW', 'REREQUEST', 'ASSIGN_FOREMAN', 'SEND_ARRIVAL_NOTICE', 'CALL_DRIVER', 'VIEW_PHOTOS', 'CONFIRM_RECEIPT'];
const REASONS: readonly ShipReasonCode[] = ['RECEIPT_DELAY', 'SHIP_INFO_MISSING', 'NO_FOREMAN', 'NO_CONTACT', 'SEND_FAILED', 'RECEIPT_ISSUE'];
const ARRIVAL_STATES: readonly ShipArrivalState[] = ['SENT', 'NO_FOREMAN', 'NO_CONTACT', 'FAILED', 'PENDING', 'NOT_NEEDED'];
const ORDER_STATUSES: readonly ShipOrderStatusCode[] = [
  'DRAFT', 'REPLY_WAIT', 'REREPLY_WAIT', 'RESEND_NEEDED', 'CONFIRMED', 'DECLINED', 'SHIP_WAIT', 'SHIP_REPLIED', 'RECEIVED', 'REPLACED',
];

function oneOf<T extends string>(v: unknown, set: readonly T[], what: string): T {
  return typeof v === 'string' && (set as readonly string[]).includes(v) ? (v as T) : fail(what);
}

/** 알 수 없는 원소만 버린다(서버가 코드를 추가해도 목록 전체가 죽지 않게). 배열이 아니면 던진다. */
function filterKnown<T extends string>(v: unknown, set: readonly T[], what: string): T[] {
  if (!Array.isArray(v)) return fail(what);
  return v.filter((x): x is T => typeof x === 'string' && (set as readonly string[]).includes(x));
}

function parseDelivery(o: Rec): ShipDelivery {
  const w = 'delivery';
  const issue = optStr(o, 'issueYn', w);
  if (issue !== null && issue !== 'Y' && issue !== 'N') fail(`${w}.issueYn`);
  return {
    orderDeliveryId: optNum(o, 'orderDeliveryId', w),
    sentDtm: optStr(o, 'sentDtm', w),
    replyDtm: optStr(o, 'replyDtm', w),
    replyRound: optNum(o, 'replyRound', w),
    arrivalDate: optStr(o, 'arrivalDate', w),
    arrivalTime: optStr(o, 'arrivalTime', w),
    prevArrivalDate: optStr(o, 'prevArrivalDate', w),
    prevArrivalTime: optStr(o, 'prevArrivalTime', w),
    driverName: optStr(o, 'driverName', w),
    driverPhone: optStr(o, 'driverPhone', w),
    photoCnt: optNum(o, 'photoCnt', w),
    issueYn: issue,
    issueNote: optStr(o, 'issueNote', w),
    replyActorId: optNum(o, 'replyActorId', w),
  };
}

function parseReceipt(o: Rec): ShipReceipt {
  const w = 'receipt';
  return {
    orderReceiptId: optNum(o, 'orderReceiptId', w),
    receiptDtm: optStr(o, 'receiptDtm', w),
    receiptDate: optStr(o, 'receiptDate', w),
    receiptMethodCode: optStr(o, 'receiptMethodCode', w),
    photoCnt: optNum(o, 'photoCnt', w),
    qtyCheckedCode: optStr(o, 'qtyCheckedCode', w),
    issueNote: optStr(o, 'issueNote', w),
    receiptActorId: optNum(o, 'receiptActorId', w),
  };
}

function parseShipRequest(o: Rec): ShipRequestInfo {
  const w = 'shipRequest';
  return {
    orderSendId: optNum(o, 'orderSendId', w),
    attemptNo: optNum(o, 'attemptNo', w),
    sendMethodCode: optStr(o, 'sendMethodCode', w),
    sentDtm: optStr(o, 'sentDtm', w),
    sendStatus: optStr(o, 'sendStatus', w),
    failReasonCode: optStr(o, 'failReasonCode', w),
    expiresDtm: optStr(o, 'expiresDtm', w),
    viewedDtm: optStr(o, 'viewedDtm', w),
    closedReasonCode: optStr(o, 'closedReasonCode', w),
  };
}

function parseArrivalNotice(v: unknown): ShipArrivalNotice {
  const w = 'arrivalNotice';
  const o = reqRec(v, w);
  return {
    state: oneOf(o.state, ARRIVAL_STATES, `${w}.state`),
    orderSendId: optNum(o, 'orderSendId', w),
    sendTypeCode: optStr(o, 'sendTypeCode', w),
    sentDtm: optStr(o, 'sentDtm', w),
    sendStatus: optStr(o, 'sendStatus', w),
    failReasonCode: optStr(o, 'failReasonCode', w),
    partnerId: optNum(o, 'partnerId', w),
  };
}

function parseItemLine(v: unknown): ShipItemLine {
  const w = 'items[]';
  const o = reqRec(v, w);
  return {
    itemSeq: optNum(o, 'itemSeq', w),
    itemName: optStr(o, 'itemName', w),
    itemSpec: optStr(o, 'itemSpec', w),
    itemQty: optNum(o, 'itemQty', w),
    itemUnit: optStr(o, 'itemUnit', w),
  };
}

function parseDeliveryItem(v: unknown): ShipDeliveryItem {
  const w = 'items[]';
  const o = reqRec(v, w);
  const d = optRecOrNull(o, 'delivery', w);
  const r = optRecOrNull(o, 'receipt', w);
  const s = optRecOrNull(o, 'shipRequest', w);
  if (!Array.isArray(o.items)) fail(`${w}.items`);
  return {
    orderVendorId: reqNum(o, 'orderVendorId', w),
    taskId: reqNum(o, 'taskId', w),
    siteId: optNum(o, 'siteId', w),
    vendorId: optNum(o, 'vendorId', w),
    vendorName: reqStr(o, 'vendorName', w),
    orderStatusCode: oneOf(o.orderStatusCode, ORDER_STATUSES, `${w}.orderStatusCode`),
    deliveryDate: optStr(o, 'deliveryDate', w),
    phaseCode: oneOf(o.phaseCode, PHASES, `${w}.phaseCode`),
    actionNeededReasons: filterKnown(o.actionNeededReasons, REASONS, `${w}.actionNeededReasons`),
    availableActions: filterKnown(o.availableActions, ACTIONS, `${w}.availableActions`),
    delivery: d ? parseDelivery(d) : null,
    receipt: r ? parseReceipt(r) : null,
    shipRequest: s ? parseShipRequest(s) : null,
    arrivalNotice: parseArrivalNotice(o.arrivalNotice),
    foremanPartnerId: optNum(o, 'foremanPartnerId', w),
    foremanName: optStr(o, 'foremanName', w),
    items: (o.items as unknown[]).map(parseItemLine),
    itemSummary: optStr(o, 'itemSummary', w),
  };
}

/** C7 응답 data 검증. 형태 이상이면 ShipParseError. */
export function shipParseList(raw: unknown): ShipDeliveryList {
  const o = reqRec(raw, 'data');
  const s = reqRec(o.summary, 'summary');
  if (!Array.isArray(o.items)) fail('items');
  return {
    summary: {
      actionNeeded: reqNum(s, 'actionNeeded', 'summary'),
      inProgress: reqNum(s, 'inProgress', 'summary'),
      received: reqNum(s, 'received', 'summary'),
      notYet: reqNum(s, 'notYet', 'summary'),
    },
    items: (o.items as unknown[]).map(parseDeliveryItem),
  };
}

/** A1 응답 data 검증(6단계가 쓰는 필드만). */
export function shipParseLedger(raw: unknown): ShipLedgerRow[] {
  const o = reqRec(raw, 'data');
  if (!Array.isArray(o.items)) fail('items');
  return (o.items as unknown[]).map(v => {
    const w = 'ledger.items[]';
    const r = reqRec(v, w);
    const rc = optRecOrNull(r, 'receipt', w);
    return {
      orderVendorId: reqNum(r, 'orderVendorId', w),
      taskId: reqNum(r, 'taskId', w),
      vendorId: optNum(r, 'vendorId', w),
      vendorName: reqStr(r, 'vendorName', w),
      orderStatusCode: oneOf(r.orderStatusCode, ORDER_STATUSES, `${w}.orderStatusCode`),
      orderDate: optStr(r, 'orderDate', w),
      deliveryDate: optStr(r, 'deliveryDate', w),
      receipt: rc
        ? {
            photoCnt: optNum(rc, 'photoCnt', 'receipt'),
            receiptDate: optStr(rc, 'receiptDate', 'receipt'),
            receiptMethodCode: optStr(rc, 'receiptMethodCode', 'receipt'),
            qtyCheckedCode: optStr(rc, 'qtyCheckedCode', 'receipt'),
          }
        : null,
    };
  });
}

/** C4a 응답 data 검증. */
export function shipParsePhotos(raw: unknown): ShipPhoto[] {
  const o = reqRec(raw, 'data');
  if (!Array.isArray(o.items)) fail('items');
  return (o.items as unknown[]).map(v => {
    const w = 'photos.items[]';
    const r = reqRec(v, w);
    const fid = r.fileId;
    if (fid !== null && fid !== undefined && typeof fid !== 'number' && typeof fid !== 'string') fail(`${w}.fileId`);
    return {
      orderPhotoId: reqNum(r, 'orderPhotoId', w),
      photoTypeCode: oneOf(r.photoTypeCode, ['LOAD', 'UNLOAD'] as const, `${w}.photoTypeCode`),
      seq: reqNum(r, 'seq', w),
      fileId: (fid ?? null) as number | string | null,
      createdAt: optStr(r, 'createdAt', w),
    };
  });
}

export function shipParseRequestResult(raw: unknown): ShipRequestResult {
  const w = 'ship-requests';
  const o = reqRec(raw, w);
  return {
    orderSendId: optNum(o, 'orderSendId', w),
    attemptNo: optNum(o, 'attemptNo', w),
    sendTypeCode: optStr(o, 'sendTypeCode', w),
    sendMethodCode: optStr(o, 'sendMethodCode', w),
    orderStatusCode: optStr(o, 'orderStatusCode', w),
    sentDtm: optStr(o, 'sentDtm', w),
    expiresDtm: optStr(o, 'expiresDtm', w),
  };
}

export function shipParseArrivalNoticeResult(raw: unknown): ShipArrivalNoticeResult {
  const o = reqRec(raw, 'arrival-notices');
  return { result: oneOf(o.result, ['SENT', 'NO_FOREMAN', 'UNCHANGED'] as const, 'arrival-notices.result') };
}

export function shipParseReceiptResult(raw: unknown): ShipReceiptResult {
  const w = 'receipts';
  const o = reqRec(raw, w);
  return {
    orderReceiptId: optNum(o, 'orderReceiptId', w),
    orderStatusCode: optStr(o, 'orderStatusCode', w),
    receiptDtm: optStr(o, 'receiptDtm', w),
    receiptDate: optStr(o, 'receiptDate', w),
    qtyCheckedCode: optStr(o, 'qtyCheckedCode', w),
  };
}

export function shipParseOrderScheduleResult(raw: unknown): ShipOrderScheduleResult {
  const w = 'order-schedule';
  const o = reqRec(raw, w);
  return {
    taskId: reqNum(o, 'taskId', w),
    orderDate: optStr(o, 'orderDate', w),
    deliveryDate: optStr(o, 'deliveryDate', w),
    affected: o.affected ?? null,
    warnings: Array.isArray(o.warnings) ? o.warnings : [],
  };
}

// ── 날짜·시각 서식 ───────────────────────────────────────────────────────────
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'] as const;
const SEOUL_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' });

/** 서울 기준 오늘(YYYY-MM-DD). 기기 시간대와 무관. */
export function shipTodaySeoul(now: Date = new Date()): string {
  return SEOUL_DATE.format(now);
}

/** "YYYY-MM-DD…" 앞 10자. 형식이 아니면 null. */
export function shipDateOnly(s: string | null | undefined): string | null {
  if (!s) return null;
  const d = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

/** "…T HH:mm…" 의 HH:mm. 'HH:mm'·'HH:mm:ss' 단독 문자열도 받는다. 형식이 아니면 null. */
export function shipTimeOnly(s: string | null | undefined): string | null {
  if (!s) return null;
  const t = s.includes('T') ? s.slice(s.indexOf('T') + 1) : s;
  const hm = t.slice(0, 5);
  return /^\d{2}:\d{2}$/.test(hm) ? hm : null;
}

function dayNumber(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
}

/** 두 날짜(YYYY-MM-DD) 사이 달력 일수 = to − from */
export function shipDayDiff(from: string, to: string): number {
  return dayNumber(to) - dayNumber(from);
}

function mdPlain(date: string): string {
  const [, m, d] = date.split('-').map(Number) as [number, number, number];
  return `${m}/${d}`;
}

/** `10/9(금)`. 날짜 형식이 아니면 null. */
export function shipFormatMd(date: string | null | undefined): string | null {
  const d = shipDateOnly(date);
  if (!d) return null;
  const wd = WEEKDAYS[new Date(dayNumber(d) * 86400000).getUTCDay()];
  return `${mdPlain(d)}(${wd})`;
}

/** `10/9 15:00`. 시각이 없으면 날짜만. 날짜 형식이 아니면 null. */
export function shipFormatMdHm(dtm: string | null | undefined): string | null {
  const d = shipDateOnly(dtm);
  if (!d) return null;
  const t = shipTimeOnly(dtm);
  return t ? `${mdPlain(d)} ${t}` : mdPlain(d);
}

/** 오늘·내일·어제·`N일 지남`·`D-N` (940 시트2 상대 표현). */
export function shipRelativeDay(date: string | null | undefined, today: string): string | null {
  const d = shipDateOnly(date);
  if (!d) return null;
  const diff = shipDayDiff(today, d);
  if (diff === 0) return '오늘';
  if (diff === 1) return '내일';
  if (diff === -1) return '어제';
  return diff > 0 ? `D-${diff}` : `${-diff}일 지남`;
}

function dtmToMinutes(dtm: string): number | null {
  const d = shipDateOnly(dtm);
  const t = shipTimeOnly(dtm);
  if (!d || !t) return null;
  const [h, mi] = t.split(':').map(Number) as [number, number];
  return dayNumber(d) * 1440 + h * 60 + mi;
}

/** 두 일시(LocalDateTime) 사이 경과 `N일 N시간 N분 뒤`(0인 단위 생략, 모두 0이면 `0분 뒤`). 값이 없거나 음수면 null. */
export function shipElapsedLabel(fromDtm: string | null | undefined, toDtm: string | null | undefined): string | null {
  if (!fromDtm || !toDtm) return null;
  const a = dtmToMinutes(fromDtm);
  const b = dtmToMinutes(toDtm);
  if (a === null || b === null || b < a) return null;
  let diff = b - a;
  const days = Math.floor(diff / 1440);
  diff -= days * 1440;
  const hours = Math.floor(diff / 60);
  const mins = diff - hours * 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}일`);
  if (hours) parts.push(`${hours}시간`);
  if (mins) parts.push(`${mins}분`);
  return `${parts.length ? parts.join(' ') : '0분'} 뒤`;
}

/** 숫자만 추출해 `010-1234-5678`·`02-123-4567` 형식으로. 형식이 맞지 않으면 원문 그대로. */
export function shipFormatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const n = raw.replace(/\D/g, '');
  if (/^02\d{7,8}$/.test(n)) return n.length === 9 ? `${n.slice(0, 2)}-${n.slice(2, 5)}-${n.slice(5)}` : `${n.slice(0, 2)}-${n.slice(2, 6)}-${n.slice(6)}`;
  if (/^0\d{2}\d{7,8}$/.test(n)) return n.length === 10 ? `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}` : `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  if (/^1\d{7}$/.test(n)) return `${n.slice(0, 4)}-${n.slice(4)}`;
  return raw;
}

// ── 요약 칩 ─────────────────────────────────────────────────────────────────
export function shipPhaseChips(s: ShipDeliveryList['summary']): { phase: ShipPhase; label: string; count: number; badge: 'danger' | 'warning' | 'success' | 'mute' }[] {
  return [
    { phase: 'ACTION_NEEDED', label: '조치 필요', count: s.actionNeeded, badge: 'danger' },
    { phase: 'IN_PROGRESS', label: '진행 중', count: s.inProgress, badge: 'warning' },
    { phase: 'RECEIVED', label: '입고 완료', count: s.received, badge: 'success' },
    { phase: 'NOT_YET', label: '요청 전', count: s.notYet, badge: 'mute' },
  ];
}

/** 행 점 색·라벨(§8.3). */
export const SHIP_PHASE_DOT: Record<ShipPhase, { tone: ShipTone; label: string }> = {
  ACTION_NEEDED: { tone: 'RED', label: '조치 필요' },
  IN_PROGRESS: { tone: 'YELLOW', label: '진행 중' },
  RECEIVED: { tone: 'GREEN', label: '입고 완료' },
  NOT_YET: { tone: 'WHITE', label: '요청 전' },
};

// ── 4칸 흐름 ────────────────────────────────────────────────────────────────
export type ShipTone = 'WHITE' | 'YELLOW' | 'RED' | 'GREEN' | 'NONE';
export interface ShipStage { key: 'REQUEST' | 'REPLY' | 'NOTICE' | 'RECEIPT'; title: string; tone: ShipTone; text: string }

const SEND_METHOD_LABEL: Record<string, string> = { AUTO: '자동', MANUAL: '수동', OFFLINE: '직접 입력' };

function isRequestFailed(r: ShipRequestInfo): boolean {
  return r.failReasonCode !== null || r.sendStatus === 'DEAD' || r.sendStatus === 'SKIPPED';
}

function stageRequest(item: ShipDeliveryItem): ShipStage {
  const title = '배송정보 요청';
  const r = item.shipRequest;
  if (!r) {
    return item.phaseCode === 'NOT_YET'
      ? { key: 'REQUEST', title, tone: 'WHITE', text: '요청 전' }
      : { key: 'REQUEST', title, tone: 'YELLOW', text: '요청 대기' };
  }
  if (isRequestFailed(r)) {
    return { key: 'REQUEST', title, tone: 'RED', text: r.failReasonCode === 'NO_CONTACT' ? '연락처 없음' : '요청 실패' };
  }
  const when = shipFormatMdHm(r.sentDtm);
  const method = r.sendMethodCode ? SEND_METHOD_LABEL[r.sendMethodCode] ?? null : null;
  const text = [when, method].filter((x): x is string => !!x).join(' ');
  return { key: 'REQUEST', title, tone: 'GREEN', text: text || '요청함' };
}

function stageReply(item: ShipDeliveryItem): ShipStage {
  const title = '거래처 회신';
  const d = item.delivery;
  if (!d) {
    return item.orderStatusCode === 'SHIP_WAIT'
      ? { key: 'REPLY', title, tone: 'YELLOW', text: '회신 대기' }
      : { key: 'REPLY', title, tone: 'WHITE', text: '—' };
  }
  const date = shipDateOnly(d.arrivalDate);
  const time = shipTimeOnly(d.arrivalTime);
  const arrive = date ? `도착 ${mdPlain(date)}${time ? ` ${time}` : ''}` : time ? `도착 ${time}` : '회신 완료';
  const parts = [arrive];
  if (d.driverName) parts.push(d.driverName);
  if (d.issueYn === 'Y') {
    parts.push('배송 이슈');
    return { key: 'REPLY', title, tone: 'YELLOW', text: parts.join(' · ') };
  }
  if (d.photoCnt !== null && d.photoCnt > 0) parts.push(`상차사진 ${d.photoCnt}장`);
  return { key: 'REPLY', title, tone: 'GREEN', text: parts.join(' · ') };
}

function stageNotice(item: ShipDeliveryItem): ShipStage {
  const title = SHIP_POPUP_TEXT.columns.notice;
  switch (item.arrivalNotice.state) {
    case 'SENT':
      return { key: 'NOTICE', title, tone: 'GREEN', text: `${item.foremanName ? `${item.foremanName} · ` : ''}안내 완료` };
    case 'NO_FOREMAN':
      return { key: 'NOTICE', title, tone: 'RED', text: '반장 미배정' };
    case 'NO_CONTACT':
      return { key: 'NOTICE', title, tone: 'RED', text: '반장 연락처 없음' };
    case 'FAILED':
      return { key: 'NOTICE', title, tone: 'RED', text: '안내 실패' };
    case 'PENDING':
      return item.delivery
        ? { key: 'NOTICE', title, tone: 'YELLOW', text: '안내 전' }
        : { key: 'NOTICE', title, tone: 'WHITE', text: '—' };
    case 'NOT_NEEDED':
      return { key: 'NOTICE', title, tone: 'WHITE', text: '—' };
  }
}

function stageReceipt(item: ShipDeliveryItem, today: string): ShipStage {
  const title = '입고';
  const r = item.receipt;
  if (r) {
    const md = shipDateOnly(r.receiptDate) ? mdPlain(shipDateOnly(r.receiptDate) as string) : null;
    if (r.qtyCheckedCode === 'ISSUE') {
      return { key: 'RECEIPT', title, tone: 'RED', text: `${md ? `${md} ` : ''}입고 · 수량 이슈` };
    }
    let text = `${md ? `${md} ` : ''}입고 완료`;
    if (r.photoCnt !== null && r.photoCnt > 0) text += ` · 하차사진 ${r.photoCnt}장`;
    if (r.receiptMethodCode === 'MANAGER') text += ' · 담당자 확인';
    return { key: 'RECEIPT', title, tone: 'GREEN', text };
  }
  const dd = shipDateOnly(item.deliveryDate);
  if (dd) {
    const diff = shipDayDiff(today, dd);
    if (diff < 0) return { key: 'RECEIPT', title, tone: 'RED', text: '입고 지연' };
    if (diff === 0) return { key: 'RECEIPT', title, tone: 'YELLOW', text: '오늘 입고 예정' };
  }
  return { key: 'RECEIPT', title, tone: 'WHITE', text: '입고 전' };
}

/** 항상 4개, 순서 고정(요청·회신·반장님 안내·입고). today = YYYY-MM-DD(서울). */
export function shipStages(item: ShipDeliveryItem, today: string): ShipStage[] {
  return [stageRequest(item), stageReply(item), stageNotice(item), stageReceipt(item, today)];
}

// ── 사유 문구 ───────────────────────────────────────────────────────────────
const REASON_TEXT: Record<ShipReasonCode, string> = {
  RECEIPT_DELAY: '입고일이 지났어요 · 입고 미확인',
  SHIP_INFO_MISSING: '오늘 입고 · 거래처 회신 없음',
  NO_FOREMAN: '반장 미배정 · 도착 안내를 보내지 못했어요',
  NO_CONTACT: '연락처가 없어 보내지 못했어요',
  SEND_FAILED: '알림톡 발송에 실패했어요',
  RECEIPT_ISSUE: '입고 수량 이슈 · 부족 또는 파손',
};

/** actionNeededReasons 순서대로 문구(서버 순서 유지). */
export function shipReasonTexts(item: ShipDeliveryItem, _today: string): string[] {
  return item.actionNeededReasons.map(c => REASON_TEXT[c]);
}

// ── 행 버튼 ─────────────────────────────────────────────────────────────────
export type ShipRowButton =
  | { kind: 'ASSIGN_FOREMAN' | 'REQUEST_NOW' | 'REREQUEST' | 'SEND_ARRIVAL_NOTICE' | 'VIEW_PHOTOS' | 'CONFIRM_RECEIPT'; label: string }
  | { kind: 'CALL_DRIVER'; label: string; disabled: true; disabledReason: string }
  | null;

/** 지시서 §8.5 우선순위. 서버 availableActions 에 있는 코드만 후보. CONFIRM_RECEIPT 는 이번 단계 제외(D8)라 고르지 않음. */
export function shipPickButton(item: ShipDeliveryItem, opts: { canAssignForeman: boolean }): ShipRowButton {
  const a = item.availableActions;
  const has = (c: ShipActionCode) => a.includes(c);
  const noticeState = item.arrivalNotice.state;
  if (has('ASSIGN_FOREMAN') && opts.canAssignForeman
    && (item.actionNeededReasons.includes('NO_FOREMAN') || noticeState === 'NO_FOREMAN')) {
    return { kind: 'ASSIGN_FOREMAN', label: '반장 배정' };
  }
  if (has('REREQUEST')) return { kind: 'REREQUEST', label: '다시 요청' };
  if (has('REQUEST_NOW')) return { kind: 'REQUEST_NOW', label: '지금 요청' };
  if (has('SEND_ARRIVAL_NOTICE') && (noticeState === 'FAILED' || noticeState === 'NO_CONTACT' || noticeState === 'PENDING')) {
    return { kind: 'SEND_ARRIVAL_NOTICE', label: '도착 안내 보내기' };
  }
  if (has('VIEW_PHOTOS') && item.phaseCode === 'RECEIVED') return { kind: 'VIEW_PHOTOS', label: '사진 보기' };
  if (has('CALL_DRIVER')) {
    return { kind: 'CALL_DRIVER', label: '기사 전화', disabled: true, disabledReason: '웹에서는 사용할 수 없어요' };
  }
  if (has('VIEW_PHOTOS')) return { kind: 'VIEW_PHOTOS', label: '사진 보기' };
  return null;
}

// ── 토스트 문구(쓰기 성공) ──────────────────────────────────────────────────
export const SHIP_TOAST = {
  requested: '배송정보를 요청했어요',
  receiptDone: '입고를 확인했어요',
  noticeSent: '반장님께 도착 안내를 보냈어요',
  noticeUnchanged: '이미 같은 내용을 안내했어요',
  noticeNoForeman: '반장님이 배정되지 않아 보내지 못했어요',
  photoNone: '사진을 찾을 수 없어요',
  photoPartial: '일부 사진을 불러오지 못했어요',
} as const;

/** 도착 안내 결과 → 토스트 문구 */
export function shipNoticeToast(result: ShipArrivalNoticeResultCode): string {
  return result === 'SENT' ? SHIP_TOAST.noticeSent : result === 'UNCHANGED' ? SHIP_TOAST.noticeUnchanged : SHIP_TOAST.noticeNoForeman;
}

/** 그 밖 화면 문구(컴포넌트에 한글 직접 기재 금지) */
export const SHIP_TEXT = {
  popupTitle: '배송정보현황 · 요청',
  emptyTitle: '배송정보를 요청할 발주가 없어요',
  focusMissing: '이 공종은 배송정보현황에 아직 없어요',
  dateUnknown: '입고일 미정',
  retry: '다시 시도',
  vendorLoadFailed: '자재파트너 정보를 불러오지 못했어요',
  photoLoadFailed: '사진을 불러오지 못했어요',
} as const;

// ── 오류 문구 (§3.5) ────────────────────────────────────────────────────────
const GENERIC_ERROR = '처리하지 못했어요. 잠시 후 다시 시도해 주세요';

interface ShipHttpError { status: number | null; code: string | null; reason: string | null }

function readHttpError(err: unknown): ShipHttpError {
  const res = (err as { response?: { status?: number; data?: { code?: unknown; data?: { reason?: unknown } | null } } } | null)?.response;
  const code = typeof res?.data?.code === 'string' ? res.data.code : null;
  const reason = typeof res?.data?.data?.reason === 'string' ? res.data.data.reason : null;
  return { status: typeof res?.status === 'number' ? res.status : null, code, reason };
}

/** 오류 → FE 문구. 서버 message 원문은 쓰지 않는다. refetch=true 면 호출 화면이 목록을 다시 조회. */
export function shipErrorMessage(err: unknown): { text: string; refetch: boolean } {
  const { status, code, reason } = readHttpError(err);
  if (status === 403) return { text: '권한이 없어요', refetch: false };
  switch (code) {
    case 'ORDER_VENDOR_NOT_FOUND':
    case 'TASK_NOT_FOUND':
    case 'SCHEDULE_NOT_FOUND':
      return { text: '이미 바뀐 항목이에요. 목록을 새로 불러왔어요', refetch: true };
    case 'CONFLICT':
      return { text: '다른 곳에서 처리 중이에요', refetch: true };
    case 'ORDER_SHIP_REQUEST_NOT_ALLOWED':
      return reason === 'NO_DELIVERY_DATE'
        ? { text: '입고 희망일이 없어서 요청하지 못했어요', refetch: true }
        : reason === 'STATUS'
          ? { text: '지금은 배송정보를 요청할 수 없는 상태예요', refetch: true }
          : { text: GENERIC_ERROR, refetch: true };
    case 'ORDER_DELIVERY_DATE_REQUIRED':
      return { text: '입고 희망일이 없어서 요청하지 못했어요', refetch: false };
    case 'ORDER_VENDOR_CONTACT_MISSING':
      return { text: '거래처 연락처가 없어서 요청하지 못했어요', refetch: false };
    case 'ORDER_SEND_FAILED':
      return { text: '알림톡을 보내지 못했어요. 잠시 후 다시 시도해 주세요', refetch: false };
    case 'ORDER_ARRIVAL_NOTICE_NOT_ALLOWED':
      return { text: '배송정보 회신 전이라 안내할 수 없어요', refetch: true };
    case 'ORDER_PHOTO_NOT_SERVED':
      return { text: SHIP_TEXT.photoLoadFailed, refetch: false };
    case 'ORDER_ALREADY_RECEIVED':
      return { text: '이미 입고 처리된 발주예요', refetch: true };
    case 'ORDER_RECEIPT_NOT_ALLOWED':
      return { text: '지금은 입고를 확인할 수 없는 상태예요', refetch: true };
    case 'VALIDATION_ERROR':
      return { text: '입력한 내용을 확인해 주세요', refetch: false };
    default:
      return { text: GENERIC_ERROR, refetch: false };
  }
}

// ── 간트 ────────────────────────────────────────────────────────────────────
/**
 * 막대 오른쪽 라벨 위치(현행 반장 라벨과 같은 계산). 오른쪽 자리가 있으면 'right', 아니면 왼쪽, 둘 다 없으면 null.
 * rightLimitPx = 다음 막대 시작(없으면 totalDays*DW), leftLimitPx = 이전 막대 끝(없으면 0), 단위 px.
 * startOff·barEnd·floatEnd 는 일 단위 인덱스(floatEnd = 대기일 표시 중이면 barEnd + leadTime, 아니면 barEnd).
 */
export function shipSideLabelPlacement(p: {
  startOff: number; barEnd: number; floatEnd: number; estWidth: number;
  rightLimitPx: number; leftLimitPx: number; DW: number;
}): 'right' | 'left' | null {
  const rightGapPx = p.rightLimitPx - p.floatEnd * p.DW;
  const leftGapPx = p.startOff * p.DW - p.leftLimitPx;
  if (rightGapPx >= p.estWidth) return 'right';
  if (leftGapPx >= p.estWidth) return 'left';
  return null;
}

/** 점선 구간 칩이 들어갈 최소 보이는 칸 수(칩 폭 근사 3칸) */
export const SHIP_MARKER_CHIP_MIN_CELLS = 3;

/**
 * ◆발주~●입고 점선 구간(§9.3). orderIdx·deliveryIdx = 간트 시작일 기준 일 인덱스(범위 밖 음수·초과 가능).
 * 호출자는 어느 한 날짜라도 없으면 호출하지 않고 구간을 그리지 않는다(-1 은 '없음'과 '시작 하루 전'이 구분되지 않음).
 * 같은 날·역전·보이는 범위에 없음 → null. days = 입고일 − 발주일(달력 일수, 보이는 범위로 잘리기 전 값).
 * showChip = 보이는 구간 길이(toIdx − fromIdx) ≥ SHIP_MARKER_CHIP_MIN_CELLS.
 */
export function shipMarkerSpan(p: { orderIdx: number; deliveryIdx: number; totalDays: number }):
  { fromIdx: number; toIdx: number; days: number; showChip: boolean } | null {
  const { orderIdx, deliveryIdx, totalDays } = p;
  if (deliveryIdx <= orderIdx) return null;
  if (totalDays <= 0) return null;
  if (deliveryIdx < 0 || orderIdx > totalDays - 1) return null;
  const fromIdx = Math.max(orderIdx, 0);
  const toIdx = Math.min(deliveryIdx, totalDays - 1);
  if (toIdx <= fromIdx) return null;
  return { fromIdx, toIdx, days: deliveryIdx - orderIdx, showChip: toIdx - fromIdx >= SHIP_MARKER_CHIP_MIN_CELLS };
}

/** `N일` 칩 글자 */
export function shipMarkerChipText(days: number): string {
  return `${days}일`;
}

/** 입고 완료 사진 아이콘 aria-label */
export function shipReceivedPhotoLabel(photoCnt: number): string {
  return `입고 사진 ${photoCnt}장`;
}

/** 간트 칩 라벨(003 옵션 표 순서·이름) */
export const SHIP_GANTT_CHIP_LABELS = { rel: '선후관계', foreman: '시공파트너', vendor: '자재파트너', markers: '발주·입고', float: '대기일' } as const;

/**
 * 시공파트너·자재파트너 칩 배타 처리(§9.2). 켜면 다른 쪽을 끄고, 켜진 쪽을 다시 누르면 둘 다 꺼진다.
 * kind = 누른 칩.
 */
export function shipToggleSidePartner(
  cur: { foreman: boolean; vendor: boolean },
  kind: 'foreman' | 'vendor',
): { foreman: boolean; vendor: boolean } {
  if (kind === 'foreman') return { foreman: !cur.foreman, vendor: false };
  return { foreman: false, vendor: !cur.vendor };
}

/** 막대 오른쪽 거래처/전화 라벨 문구. 활성 행 N곳이면 `외 N-1곳`. 전화가 없으면 이름만. */
export function shipVendorLabel(rows: { vendorName: string; vendorPhone: string | null }[]): string | null {
  const first = rows[0];
  if (!first) return null;
  const phone = first.vendorPhone ? shipFormatPhone(first.vendorPhone) : '';
  const base = phone ? `${first.vendorName}/${phone}` : first.vendorName;
  return rows.length > 1 ? `${base} 외 ${rows.length - 1}곳` : base;
}

// ── C-4 팝업 보조 문구(접근성 이름·조립 문구) ──
export const SHIP_POPUP_TEXT = {
  taskNameMissing: '-',
  summaryLabel: '배송정보 요약',
  listLabel: '배송정보 목록',
  /** 열 머리(P1-170) — 행의 4칸 제목과 같은 글자 */
  columns: {
    head: '공종 · 자재파트너 / 입고일',
    request: '배송정보 요청',
    reply: '거래처 회신',
    notice: '반장님 안내',
    receipt: '입고',
    action: '조치',
  },
  directCount: (n: number) => `별도발주 공정 ${n}`,
  /** 자재파트너 시공 공종이 있는 현장에서만 목록 위에 보이는 안내 */
  vendorConstructNote: '자재파트너가 시공하는 공종은 배송정보요청 · 입고확인 대상이 아니에요',
  directReceiptLink: '직접 입고 확인',
} as const;

/** `입고 10/9(금) · 내일` — 날짜가 없으면 '입고일 미정'. */
export function shipPopupDeliveryLine(ymd: string | null, today: string): string {
  const md = shipFormatMd(ymd);
  if (!md) return SHIP_TEXT.dateUnknown;
  const rel = shipRelativeDay(ymd, today);
  return rel ? `입고 ${md} · ${rel}` : `입고 ${md}`;
}

/** 사진 보기 제목. note 가 있으면(입고 기록 요약) 뒤에 덧붙인다. */
export function shipPopupPhotoTitle(taskName: string, vendorName: string, note?: string | null): string {
  const base = `${taskName} · ${vendorName} 사진`;
  return note ? `${base} · ${note}` : base;
}

const RECEIPT_WHO: Record<string, string> = { MANAGER: '담당자', WORK_REPORT: '작업보고' };

/**
 * 사진 보기에 붙이는 입고 기록 요약 — 등록자(반장 링크는 반장 이름, 담당자·작업보고)·수량 확인·부족·파손 내용(issueNote).
 * 입고 기록이 없거나 알 수 있는 값이 없으면 null(값을 만들어 채우지 않음).
 */
export function shipReceiptPhotoNote(item: ShipDeliveryItem): string | null {
  const r = item.receipt;
  if (!r) return null;
  const parts: string[] = [];
  const who = r.receiptMethodCode === 'FOREMAN_LINK'
    ? (item.foremanName ? `${item.foremanName} 반장님` : '반장님')
    : r.receiptMethodCode ? RECEIPT_WHO[r.receiptMethodCode] ?? null : null;
  if (who) parts.push(`등록 ${who}`);
  if (r.qtyCheckedCode === 'NORMAL') parts.push(`수량 확인 ${SHIP_RECEIPT_TEXT.qtyNormal}`);
  else if (r.qtyCheckedCode === 'ISSUE') {
    parts.push(`수량 확인 ${SHIP_RECEIPT_TEXT.qtyIssue}`);
    const note = r.issueNote?.trim();
    if (note) parts.push(`내용 ${note}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** 행 대표 버튼과 별개로 노출하는 보조 링크(서버 availableActions 에 CONFIRM_RECEIPT 가 있을 때만). */
export function shipSecondaryLink(item: ShipDeliveryItem): { kind: 'CONFIRM_RECEIPT'; label: string } | null {
  return item.availableActions.includes('CONFIRM_RECEIPT')
    ? { kind: 'CONFIRM_RECEIPT', label: SHIP_POPUP_TEXT.directReceiptLink }
    : null;
}

// ── 담당자 직접 입고 확인 창(E-31) ──
export const SHIP_RECEIPT_TEXT = {
  title: '직접 입고 확인',
  qtyLabel: '수량 확인',
  qtyNormal: '이상 없음',
  qtyIssue: '부족·파손',
  noteLabel: '부족·파손 내용',
  noteCount: (n: number, max: number) => `${n}/${max}`,
  dateLabel: '입고일',
  submit: '입고 확인',
  cancel: '취소',
  qtyRequired: '수량 확인을 선택해 주세요',
  noteRequired: '부족·파손 내용을 입력해 주세요',
  noteMax: '내용은 300자까지 쓸 수 있어요',
  dateRequired: '입고일을 정해 주세요',
  dateRange: '입고일은 오늘부터 30일 전까지 고를 수 있어요',
} as const;

export const SHIP_RECEIPT_NOTE_MAX = 300;
const SHIP_RECEIPT_PAST_DAYS = 30;

/** 직접 입고 확인 입력값. qty 는 선택 전 '' (기본값으로 가정하지 않음), receiptDate 는 열 때 오늘로 시작. */
export interface ShipReceiptForm { qty: 'NORMAL' | 'ISSUE' | ''; issueNote: string; receiptDate: string }

export function shipReceiptFormInit(today: string): ShipReceiptForm {
  return { qty: '', issueNote: '', receiptDate: today };
}

export interface ShipReceiptFormErrors { qty?: string; issueNote?: string; receiptDate?: string }

/** 서버 검증과 같은 기준 — 수량 확인 필수, 부족·파손이면 내용 필수(공백 제거 후 1~300자), 입고일은 오늘 이전 30일 이내. */
export function shipReceiptFormErrors(f: ShipReceiptForm, today: string): ShipReceiptFormErrors {
  const out: ShipReceiptFormErrors = {};
  if (f.qty !== 'NORMAL' && f.qty !== 'ISSUE') out.qty = SHIP_RECEIPT_TEXT.qtyRequired;
  if (f.qty === 'ISSUE') {
    const n = f.issueNote.trim().length;
    if (n === 0) out.issueNote = SHIP_RECEIPT_TEXT.noteRequired;
    else if (n > SHIP_RECEIPT_NOTE_MAX) out.issueNote = SHIP_RECEIPT_TEXT.noteMax;
  }
  const d = shipDateOnly(f.receiptDate);
  if (!d) out.receiptDate = SHIP_RECEIPT_TEXT.dateRequired;
  else {
    const diff = shipDayDiff(today, d);
    if (diff > 0 || diff < -SHIP_RECEIPT_PAST_DAYS) out.receiptDate = SHIP_RECEIPT_TEXT.dateRange;
  }
  return out;
}

/** 검증을 통과한 입력 → C6 본문(사진 없음). 오류가 있으면 null. */
export function shipReceiptBody(f: ShipReceiptForm, today: string): ShipReceiptBody | null {
  const errs = shipReceiptFormErrors(f, today);
  if (Object.keys(errs).length > 0 || (f.qty !== 'NORMAL' && f.qty !== 'ISSUE')) return null;
  return f.qty === 'ISSUE'
    ? { qtyCheckedCode: 'ISSUE', issueNote: f.issueNote.trim(), receiptDate: f.receiptDate }
    : { qtyCheckedCode: 'NORMAL', receiptDate: f.receiptDate };
}

export function shipPopupPhotoAlt(kind: 'LOAD' | 'UNLOAD', n: number): string {
  return `${kind === 'LOAD' ? '상차사진' : '하차사진'} ${n}`;
}

/** 4칸 글자 안의 '상차사진 N장'·'하차사진 N장' 을 글자 링크로 쪼갠다. */
export const SHIP_PHOTO_LINK_RE = /(상차사진 \d+장|하차사진 \d+장)/;

export function shipPopupLinkKind(piece: string): 'LOAD' | 'UNLOAD' | null {
  if (piece.startsWith('상차사진')) return 'LOAD';
  if (piece.startsWith('하차사진')) return 'UNLOAD';
  return null;
}
